import { StandardEvents } from '@shopify/events';

/**
 * Product cards: build the per-colour photo slides on demand.
 *
 * Horizon wrote a hidden slide for every colour into every card, so a swatch tap could un-hide that colour's photo
 * (product-card.js #updateVariantImages: the slide whose slide-id matches the swatch's media id is shown, the other
 * [variant-image] slides are hidden). Those slides were most of a card's HTML. snippets/card-gallery.liquid now lists
 * them as data (script[data-sc-variant-slides]: id, 1200px URL, alt, size, aspect ratio, focal point) and this module
 * builds them, as copies of the card's first slide with the colour's image, the first time a pointer enters the card,
 * a finger touches it or keyboard focus reaches it: always before a swatch can be chosen (and again, as a safety net,
 * on Horizon's variant-selected event, which reaches the document before the card's own handler).
 *
 * Same markup, classes, image widths and sizes as Horizon's slides, lazy loaded, hidden until chosen. Cards added
 * later (infinite scroll, Load more, re-rendered sections) need nothing: the listeners are on the document.
 * Ported from MLB's assets/mlb-card-slides.js (21 Oct 2026).
 */
const CARD_IMAGE_WIDTHS = [240, 352, 480, 640, 832, 1200];

/**
 * @param {string} url - an image URL with a width parameter
 * @param {number} width
 */
const withWidth = (url, width) => (/[?&]width=\d+/.test(url) ? url.replace(/([?&]width=)\d+/, `$1${width}`) : `${url}${url.includes('?') ? '&' : '?'}width=${width}`);

/**
 * @typedef {{ i?: number, id: number, u: string, alt?: string, w?: number, h?: number, r?: number, fp?: string }} DeferredSlide
 */

/**
 * A slide's place in the product's media list: Horizon names each slide's timeline after it (--slide-N)
 * @param {Element} slide
 */
const slidePosition = (slide) => {
  const match = /--slide-(\d+)/.exec(slide instanceof HTMLElement ? slide.style.getPropertyValue('--slideshow-timeline') : '');
  return match ? Number(match[1]) : Infinity;
};

/** @param {Element} card */
function buildSlides(card) {
  const data = card.querySelector('script[data-sc-variant-slides]');
  if (!data) return;
  data.remove(); // once per card

  const container = card.querySelector('slideshow-slides');
  // copy a photo slide (the first one, unless the card leads with a video or 3D model)
  const template = [...(container?.querySelectorAll(':scope > slideshow-slide') ?? [])].find(
    (slide) => slide.querySelector('img') && !slide.querySelector('video, model-viewer, deferred-media, iframe')
  );
  if (!container || !template) return;

  /** @type {DeferredSlide[]} */
  let list;
  try {
    list = JSON.parse(data.textContent || '[]');
  } catch {
    return;
  }

  const templateImage = template.querySelector('img');
  const baseSizes = (templateImage?.getAttribute('sizes') || '').replace(/^auto,\s*/, '');
  let fallbackIndex = container.querySelectorAll(':scope > slideshow-slide').length;

  for (const media of list) {
    const id = String(media.id);
    if (container.querySelector(`:scope > slideshow-slide[slide-id="${CSS.escape(id)}"]`)) continue;

    const position = Number.isInteger(media.i) ? Number(media.i) : fallbackIndex++;
    const slide = /** @type {HTMLElement} */ (template.cloneNode(true));
    slide.setAttribute('slide-id', id);
    slide.setAttribute('variant-image', '');
    slide.setAttribute('aria-hidden', 'true');
    slide.hidden = true;
    slide.style.setProperty('--slideshow-timeline', `--slide-${position}`);

    const holder = slide.querySelector('.product-media');
    if (holder instanceof HTMLElement) {
      holder.dataset.mediaId = id;
      if (media.r) holder.style.setProperty('--ratio', String(media.r));
    }

    const image = slide.querySelector('img');
    if (image) {
      image.removeAttribute('fetchpriority');
      image.loading = 'lazy';
      image.alt = media.alt || '';
      image.srcset = CARD_IMAGE_WIDTHS.map((width) => `${withWidth(media.u, width)} ${width}w`).join(', ');
      image.sizes = baseSizes ? `auto, ${baseSizes}` : 'auto';
      image.src = withWidth(media.u, 1200);
      if (media.w) image.width = media.w;
      if (media.h) image.height = media.h;
      if (media.fp) image.style.setProperty('--focal-point', media.fp);
    }

    // back in its place in the media list, as Horizon rendered it (the order a hover or swipe steps through)
    const next = [...container.querySelectorAll(':scope > slideshow-slide')].find((other) => slidePosition(other) > position);
    container.insertBefore(slide, next ?? null);
  }
}

/** @param {Event} event */
const onIntent = (event) => {
  const card = event.target instanceof Element ? event.target.closest('product-card') : null;
  if (card) buildSlides(card);
};

// Capture phase on the document: runs before the card (and its swatches) handle the same event
for (const type of ['pointerenter', 'pointerdown', 'focusin', StandardEvents.productSelect]) {
  document.addEventListener(type, onIntent, true);
}
